import type BpmnModeler from 'bpmn-js/lib/Modeler'
import type NavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	type ImprovementOverlayActivate,
	useImprovementOverlays,
} from '@/components/bpmn/hooks/useImprovementOverlays'
import { isBpmnElementOffScreen, useSpotlight } from '@/components/bpmn/hooks/useSpotlight'
import { openImprovementsOnElement } from '@/components/bpmn/improvements/chooseImprovementBadge'
import { ImprovementCanvasSummary } from '@/components/bpmn/improvements/ImprovementCanvasSummary'
import { useImprovementOverlay } from '@/components/bpmn/improvements/ImprovementOverlayContext'
import {
	ImprovementReviewCard,
	type ReviewCardPositionAnchor,
} from '@/components/bpmn/improvements/ImprovementReviewCard'
import { ImprovementSpotlightLegend } from '@/components/bpmn/improvements/ImprovementSpotlightLegend'
import {
	countOnModelImprovements,
	countProcessLevelImprovements,
	groupImprovementsByBpmnElement,
	isDismissedImprovement,
	isProcessLevelImprovement,
	primaryBpmnIdForItem,
	relatedStepsForItem,
} from '@/components/bpmn/improvements/mapImprovementsToElements'

const VISIBLE_STORAGE_KEY = 'mm.canvas.improvementsVisible'
const EMPTY_NUMBER_MAP = new Map<string, string>()
const EMPTY_TITLE_MAP = new Map<string, string>()

type BpmnViewer = BpmnModeler | InstanceType<typeof NavigatedViewer>

interface ElementLike {
	id: string
	type: string
}

interface ImprovementCanvasLayerProps {
	modeler: BpmnViewer | null
	xmlLoaded: boolean
	boundary: HTMLElement | null
}

interface OpenCardState {
	bpmnId: string
	itemId: string
	anchor: HTMLButtonElement
}

interface GraphicsRegistry {
	getGraphics: (id: string) => Element | undefined
}

function graphicsAnchor(modeler: BpmnViewer, bpmnId: string): ReviewCardPositionAnchor | null {
	try {
		const registry = modeler.get('elementRegistry') as GraphicsRegistry
		const gfx = registry.getGraphics(bpmnId)
		if (!gfx) return null
		return {
			contextElement: gfx,
			getBoundingClientRect: () => {
				const live = registry.getGraphics(bpmnId)
				return live?.getBoundingClientRect() ?? new DOMRect()
			},
		}
	} catch {
		return null
	}
}

function readVisiblePreference(): boolean {
	if (typeof window === 'undefined') return true
	try {
		const raw = localStorage.getItem(VISIBLE_STORAGE_KEY)
		if (raw === null) return true
		return raw !== 'false'
	} catch {
		return true
	}
}

function isFlowStepType(type: string): boolean {
	if (type === 'label') return false
	return /Task|Gateway|Event|SubProcess|CallActivity|Transaction/.test(type)
}

function listDiagramElementIds(modeler: BpmnViewer | null): string[] {
	if (!modeler) return []
	try {
		const registry = modeler.get('elementRegistry') as {
			filter: (fn: (el: ElementLike) => boolean) => ElementLike[]
		}
		return registry.filter((el) => isFlowStepType(el.type)).map((el) => el.id)
	} catch {
		return []
	}
}

function focusBpmnElement(modeler: BpmnViewer, bpmnId: string): boolean {
	try {
		const registry = modeler.get('elementRegistry') as { get: (id: string) => unknown }
		const canvas = modeler.get('canvas') as { scrollToElement: (el: unknown) => void }
		const selection = modeler.get('selection') as { select: (el: unknown) => void }
		const el = registry.get(bpmnId)
		if (!el) return false
		selection.select(el)
		canvas.scrollToElement(el)
		return true
	} catch {
		return false
	}
}

export function ImprovementCanvasLayer({
	modeler,
	xmlLoaded,
	boundary,
}: ImprovementCanvasLayerProps) {
	const ctx = useImprovementOverlay()
	const [visible, setVisible] = useState(readVisiblePreference)
	const [diagramIds, setDiagramIds] = useState<string[]>([])
	const [openCard, setOpenCard] = useState<OpenCardState | null>(null)
	const [hoveredBpmnId, setHoveredBpmnId] = useState<string | null>(null)
	const [fullscreenRoot, setFullscreenRoot] = useState<HTMLElement | null>(null)
	const returnFocusRef = useRef<HTMLButtonElement | null>(null)
	const badgeElementsRef = useRef<Map<string, HTMLButtonElement>>(new Map())

	useEffect(() => {
		const syncFullscreenRoot = () => {
			setFullscreenRoot(
				document.fullscreenElement instanceof HTMLElement ? document.fullscreenElement : null
			)
		}
		syncFullscreenRoot()
		document.addEventListener('fullscreenchange', syncFullscreenRoot)
		return () => document.removeEventListener('fullscreenchange', syncFullscreenRoot)
	}, [])

	const items = ctx?.items ?? []
	const triage = ctx?.triage
	const numberMap = ctx?.numberMap ?? EMPTY_NUMBER_MAP
	const titleMap = ctx?.titleMap ?? EMPTY_TITLE_MAP
	const orderedItems = ctx?.orderedItems ?? items

	const getStatusFromTriage = triage?.getStatus
	const getStatus = useCallback(
		(item: (typeof items)[number]) => {
			if (!getStatusFromTriage) return item.status
			return getStatusFromTriage(item.id, item)
		},
		[getStatusFromTriage]
	)

	useEffect(() => {
		if (!modeler || !xmlLoaded) {
			setDiagramIds([])
			return
		}
		const refreshIds = () => setDiagramIds(listDiagramElementIds(modeler))
		refreshIds()
		const eventBus = modeler.get('eventBus') as {
			on: (event: string, cb: () => void) => void
			off: (event: string, cb: () => void) => void
		}
		eventBus.on('commandStack.changed', refreshIds)
		return () => eventBus.off('commandStack.changed', refreshIds)
	}, [modeler, xmlLoaded])

	const hasTriage = Boolean(triage)
	const grouped = useMemo(() => {
		if (!hasTriage) return new Map<string, typeof items>()
		return groupImprovementsByBpmnElement(items, diagramIds, getStatus, numberMap)
	}, [diagramIds, getStatus, hasTriage, items, numberMap])

	const onModelCount = useMemo(
		() => (triage ? countOnModelImprovements(items, diagramIds, getStatus) : 0),
		[diagramIds, getStatus, items, triage]
	)
	const processLevelCount = useMemo(
		() => (triage ? countProcessLevelImprovements(items, diagramIds, getStatus) : 0),
		[diagramIds, getStatus, items, triage]
	)

	const handleActivate = useCallback((payload: ImprovementOverlayActivate) => {
		returnFocusRef.current = payload.button
		setOpenCard({
			bpmnId: payload.bpmnId,
			itemId: payload.badge.primary.id,
			anchor: payload.button,
		})
	}, [])

	const elementItems = openCard ? (grouped.get(openCard.bpmnId) ?? []) : []
	const cardItem = openCard
		? (elementItems.find((item) => item.id === openCard.itemId) ?? null)
		: null
	const liveAnchor: HTMLButtonElement | null = (() => {
		if (!openCard) return null
		const fromRef = badgeElementsRef.current.get(openCard.bpmnId) ?? null
		if (fromRef && document.contains(fromRef)) return fromRef
		if (document.contains(openCard.anchor)) return openCard.anchor
		return fromRef
	})()
	const positionBpmnId = openCard?.bpmnId
	const positionAnchor = useMemo(() => {
		if (!positionBpmnId || !modeler) return liveAnchor
		return graphicsAnchor(modeler, positionBpmnId) ?? liveAnchor
	}, [liveAnchor, modeler, positionBpmnId])

	const relatedSteps = useMemo(() => {
		if (!cardItem || !visible) return []
		return relatedStepsForItem(cardItem, diagramIds, numberMap, titleMap)
	}, [cardItem, diagramIds, numberMap, titleMap, visible])

	useEffect(() => {
		setHoveredBpmnId(null)
	}, [cardItem?.id])

	const spotlightApply = useMemo(() => {
		if (!openCard || !cardItem || !visible) return null
		const primaryBpmnId = primaryBpmnIdForItem(cardItem, diagramIds)
		if (!primaryBpmnId) return null
		return {
			primaryBpmnId,
			relatedBpmnIds: relatedSteps.flatMap((step) => (step.bpmnId ? [step.bpmnId] : [])),
			type: cardItem.type ?? 'question',
			numberMap,
		}
	}, [cardItem, diagramIds, numberMap, openCard, relatedSteps, visible])

	useImprovementOverlays(
		ctx ? modeler : null,
		xmlLoaded,
		grouped,
		getStatus,
		visible && Boolean(ctx),
		orderedItems,
		handleActivate,
		badgeElementsRef
	)

	const { isActive: spotlightActive } = useSpotlight(
		ctx ? modeler : null,
		xmlLoaded,
		spotlightApply,
		hoveredBpmnId,
		setHoveredBpmnId
	)

	const closeCard = useCallback(() => {
		setOpenCard(null)
		setHoveredBpmnId(null)
		const el = returnFocusRef.current
		if (el && document.contains(el)) {
			el.focus()
		}
	}, [])

	useEffect(() => {
		if (!openCard || !modeler) return
		const rebind = () => {
			const live = badgeElementsRef.current.get(openCard.bpmnId)
			if (!live || !document.contains(live)) return
			setOpenCard((prev) => {
				if (!prev || prev.bpmnId !== openCard.bpmnId || prev.anchor === live) return prev
				return { ...prev, anchor: live }
			})
			returnFocusRef.current = live
		}
		rebind()
		const eventBus = modeler.get('eventBus') as {
			on: (event: string, cb: () => void) => void
			off: (event: string, cb: () => void) => void
		}
		eventBus.on('commandStack.changed', rebind)
		return () => eventBus.off('commandStack.changed', rebind)
	}, [grouped, modeler, openCard, visible, xmlLoaded])

	const toggleVisible = useCallback(() => {
		setVisible((prev) => {
			const next = !prev
			try {
				localStorage.setItem(VISIBLE_STORAGE_KEY, String(next))
			} catch {
				// Ignore quota / private mode.
			}
			if (!next) setOpenCard(null)
			return next
		})
	}, [])

	const scrollToIssue = useCallback(
		(issueId: string): boolean => {
			if (!modeler || !ctx) return false
			const item = items.find((entry) => entry.id === issueId)
			if (!item) return false
			const bpmnId = primaryBpmnIdForItem(item, diagramIds)
			if (!bpmnId) return false
			return focusBpmnElement(modeler, bpmnId)
		},
		[ctx, diagramIds, items, modeler]
	)

	const openIssueOnCanvas = useCallback(
		(issueId: string): boolean => {
			if (!modeler) return false
			const item = items.find((entry) => entry.id === issueId)
			if (!item) return false
			const bpmnId = primaryBpmnIdForItem(item, diagramIds)
			if (!bpmnId) return false
			if (!focusBpmnElement(modeler, bpmnId)) return false
			setVisible(true)
			try {
				localStorage.setItem(VISIBLE_STORAGE_KEY, 'true')
			} catch {
				// Ignore quota / private mode.
			}
			const button = badgeElementsRef.current.get(bpmnId)
			if (!button) return false
			returnFocusRef.current = button
			setOpenCard({ bpmnId, itemId: issueId, anchor: button })
			return true
		},
		[diagramIds, items, modeler]
	)

	useEffect(() => {
		if (!ctx) return
		ctx.registerCanvasApi({ scrollToIssue })
		return () => ctx.registerCanvasApi(null)
	}, [ctx, scrollToIssue])

	// Show in model: if badges are hidden, turn them on and return; the next run
	// opens the card after overlays have mounted.
	useEffect(() => {
		const issueId = ctx?.pendingOpenIssueId
		if (!issueId || !xmlLoaded || diagramIds.length === 0 || !ctx) return
		if (items.length === 0) return
		const item = items.find((entry) => entry.id === issueId)
		const bpmnId = item ? primaryBpmnIdForItem(item, diagramIds) : undefined
		if (!item) {
			ctx.consumePendingOpen()
			return
		}
		if (!bpmnId) {
			ctx.expandIssue(item.id)
			ctx.consumePendingOpen()
			return
		}
		if (!grouped.has(bpmnId)) {
			if (getStatus(item) === 'dismissed') ctx.consumePendingOpen()
			return
		}
		if (!visible) {
			setVisible(true)
			try {
				localStorage.setItem(VISIBLE_STORAGE_KEY, 'true')
			} catch {
				// Ignore quota / private mode.
			}
			return
		}
		if (openIssueOnCanvas(issueId)) {
			ctx.consumePendingOpen()
		}
	}, [ctx, diagramIds, getStatus, grouped, items, openIssueOnCanvas, visible, xmlLoaded])

	const handleFocusRelated = useCallback(
		(bpmnId: string) => {
			if (!modeler) return
			if (!isBpmnElementOffScreen(modeler, bpmnId)) return
			try {
				const registry = modeler.get('elementRegistry') as { get: (id: string) => unknown }
				const canvas = modeler.get('canvas') as { scrollToElement: (el: unknown) => void }
				const el = registry.get(bpmnId)
				if (el) canvas.scrollToElement(el)
			} catch {
				// Element may have been deleted.
			}
		},
		[modeler]
	)

	const handleAddressed = useCallback(() => {
		if (!openCard) return
		const onElement = grouped.get(openCard.bpmnId) ?? []
		const remaining = openImprovementsOnElement(onElement, getStatus).filter(
			(entry) => entry.id !== openCard.itemId
		)
		const next = remaining[0]
		if (next) {
			setOpenCard({ ...openCard, itemId: next.id })
			return
		}
		closeCard()
	}, [closeCard, getStatus, grouped, openCard])

	if (!ctx || !triage) return null

	const elementIndex = cardItem ? elementItems.findIndex((item) => item.id === cardItem.id) : 0
	const globalIndex = cardItem ? orderedItems.findIndex((item) => item.id === cardItem.id) : 0

	return (
		<>
			<div className="pointer-events-none absolute top-3 right-3 z-[15] flex flex-col items-end">
				<ImprovementCanvasSummary
					onModelCount={onModelCount}
					processLevelCount={processLevelCount}
					visible={visible}
					onToggleVisible={toggleVisible}
					onProcessLevelClick={() => {
						const ids = items
							.filter(
								(item) =>
									!isDismissedImprovement(item, getStatus) && isProcessLevelImprovement(item, diagramIds)
							)
							.map((item) => item.id)
						ctx.showProcessLevel(ids)
					}}
				/>
				{spotlightActive && cardItem ? (
					<ImprovementSpotlightLegend type={cardItem.type ?? 'question'} />
				) : null}
			</div>
			{openCard && cardItem && boundary && liveAnchor && positionAnchor
				? createPortal(
						<ImprovementReviewCard
							item={cardItem}
							idx={Math.max(0, globalIndex)}
							total={Math.max(1, orderedItems.length)}
							elementIndex={Math.max(0, elementIndex)}
							elementTotal={elementItems.length}
							anchor={positionAnchor}
							clickAnchor={liveAnchor}
							boundary={boundary}
							spotlight={spotlightApply}
							triage={triage}
							readOnly={ctx.readOnly}
							onClose={closeCard}
							onExpand={() => {
								ctx.expandIssue(cardItem.id)
								setOpenCard(null)
							}}
							onAddressed={handleAddressed}
							onElementPrev={() => {
								const prev = elementItems[elementIndex - 1]
								if (prev) setOpenCard({ ...openCard, itemId: prev.id })
							}}
							onElementNext={() => {
								const next = elementItems[elementIndex + 1]
								if (next) setOpenCard({ ...openCard, itemId: next.id })
							}}
							relatedSteps={relatedSteps}
							hoveredBpmnId={hoveredBpmnId}
							onHoverRelated={setHoveredBpmnId}
							onFocusRelated={handleFocusRelated}
						/>,
						fullscreenRoot ?? document.body
					)
				: null}
		</>
	)
}
